import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../../lib/api-client.js';

export interface ObligationPayment {
  amount_paid: number;
  date_paid: string;
  confirmation_number?: string;
}

export interface Obligation {
  id: string;
  obligation_name: string;
  vendor?: string;
  category?: string;
  amount: number;
  frequency?: string;
  due_date?: string;
  status: 'OVERDUE' | 'DUE_SOON' | 'UPCOMING' | 'PAID';
  days_until_due: number | null;
  reference_number?: string;
  terminal?: string;
  notes?: string;
  auto_renew?: boolean;
  last_payment: ObligationPayment | null;
}

export interface ObligationSummary {
  total_due_this_period: number;
  overdue_count: number;
  overdue_total: number;
  due_soon_count: number;
  due_soon_total: number;
  upcoming_count: number;
  upcoming_total: number;
  paid_count: number;
  paid_total: number;
}

export interface ObligationByCategory {
  category: string;
  total: number;
  count: number;
}

export interface ObligationCalendarDay {
  date: string;
  total_due: number;
  items: string[];
}

export interface ObligationWeekOutlook {
  week_start: string;
  week_end: string;
  by_category: { category: string; total: number }[];
  total: number;
}

export interface ObligationsData {
  as_of: string;
  range_days: number;
  obligations: Obligation[];
  summary: ObligationSummary;
  by_category: ObligationByCategory[];
  calendar: ObligationCalendarDay[];
  weekly_outlook: ObligationWeekOutlook[];
}

interface Response extends ObligationsData { error?: string; }

export function useFinanceObligations(rangeDays: number) {
  const [data, setData] = useState<ObligationsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams();
      params.set('range', String(rangeDays));
      const resp = await apiFetch<Response>(`/api/finance/obligations?${params.toString()}`);
      if (resp?.error) throw new Error(resp.error);
      setData(resp);
    } catch (err) { setData(null); setError(err instanceof Error ? err.message : 'Obligations load failed'); }
    finally { setLoading(false); }
  }, [rangeDays]);

  useEffect(() => { load(); }, [load]);
  return { data, loading, error, refresh: load };
}
